import { Mobile } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import Popover from "@govlaunch/popover";
import ProfilePicture from "@govlaunch/profile-picture";
import { IGetGovernmentQuery } from "../../lib/queries/__generated__/GetGovernmentQuery.generated";
import apostrophe from "../../lib/utils/apostrophe";
import DelayedHover from "../DelayedHover";
import Container from "../layout/Container";
import { getGovlaunchUrl } from "../utils/getGovlaunchUrl";

interface IStackProps {
  government: NonNullable<IGetGovernmentQuery["government"]>;
}
export default function Stack({ government }: IStackProps) {
  return (
    <Mobile>
      {(isMobile: boolean) => (
        <Container>
          <div
            css={{
              display: "flex",
              backgroundColor: "white",
              justifyContent: "space-between",
              borderRadius: 5,
              border: `solid 1px ${palette.lightestGray}`,
              flexDirection: "column",
              alignItems: "flex-start"
            }}
          >
            <div
              css={{
                padding: 15,
                borderBottom: `solid 1px ${palette.lightestGray}`,
                display: "flex",
                justifyContent: "space-between",
                width: "100%"
              }}
            >
              <span
                css={{
                  textTransform: "uppercase",
                  fontSize: 12,
                  color: palette.sealBlue,
                  letterSpacing: 0.5,
                  fontWeight: 500
                }}
              >
                {apostrophe(government.city.name)} Stack ({(government.products || []).length})
              </span>
            </div>

            {isMobile ? (
              <div
                css={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  padding: 15,
                  gridRowGap: 15
                }}
              >
                {(government.products || []).map((product) => (
                  <div
                    key={product._id}
                    css={{
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <a href={`${getGovlaunchUrl()}/products/${product.slug}`} target="_blank" rel="noopener noreferrer">
                      <ProfilePicture
                        image={product.logo}
                        name={product.name}
                        size={60}
                        disruptor={product.isDisruptor || false}
                        css={{
                          boxShadow: "0 4px 10px 0 #00000033"
                        }}
                      />
                    </a>

                    <div
                      css={{
                        marginLeft: 10,
                        display: "flex",
                        flexDirection: "column"
                      }}
                    >
                      <a
                        href={`${getGovlaunchUrl()}/products/${product.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        css={{
                          textDecoration: "none"
                        }}
                      >
                        <span
                          css={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: palette.text
                          }}
                        >
                          {product.name}
                        </span>
                      </a>

                      {product.company && (
                        <a
                          href={`${getGovlaunchUrl()}/vendors/${product.company.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          css={{
                            textDecoration: "none"
                          }}
                        >
                          <span
                            css={{
                              fontSize: 12,
                              fontWeight: 400,
                              color: palette.mediumGray,
                              marginTop: 2.5
                            }}
                          >
                            from {product.company?.name}
                          </span>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                css={{
                  display: "grid",
                  gridTemplateColumns: "repeat(11, 1fr)",
                  padding: 15,
                  gridColumnGap: 15,
                  gridRowGap: 15
                }}
              >
                {(government.products || []).map((product) => {
                  return (
                    <a
                      key={product._id}
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`${getGovlaunchUrl()}/products/${product.slug}`}
                      css={{
                        display: "inline-flex"
                      }}
                    >
                      <DelayedHover ms={300}>
                        {({ hover, ...bind }) => (
                          <Popover
                            placement="top"
                            isOpen={hover}
                            backgroundColor={palette.innovatorBlue}
                            borderColor={palette.innovatorBlue}
                            boxStyle={{
                              boxShadow: "0 10px 40px 0 rgba(34, 35, 40, 0.2)"
                            }}
                            render={() => (
                              <div
                                css={{
                                  padding: 10,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-start",
                                  color: palette.white
                                }}
                              >
                                <span
                                  css={{
                                    fontSize: 14,
                                    fontWeight: 600
                                  }}
                                >
                                  {product.name}
                                </span>

                                {product.company && (
                                  <span
                                    css={{
                                      fontSize: 12,
                                      fontWeight: 400
                                    }}
                                  >
                                    from {product.company.name}
                                  </span>
                                )}
                              </div>
                            )}
                          >
                            {({ innerRef }: { innerRef: any }) => {
                              return (
                                <ProfilePicture
                                  {...bind}
                                  innerRef={innerRef}
                                  size={60}
                                  image={product.logo}
                                  name={product.name}
                                  css={{
                                    boxShadow: "0 4px 10px 0 #00000033"
                                  }}
                                />
                              );
                            }}
                          </Popover>
                        )}
                      </DelayedHover>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </Container>
      )}
    </Mobile>
  );
}
